// Inbox → extraction-pipeline routing — visual flowchart editor (admin).
// Backend: app/routes/intake_routing.py. The graph IS the editor: click an inbox,
// then a pipeline, to toggle a route; click a route line to remove it.

import { useCallback, useEffect, useRef, useState } from 'react';
import cytoscape, {
  type Core,
  type ElementDefinition,
  type EventObject,
} from 'cytoscape';
import dagre from 'cytoscape-dagre';

import { intakeRoutingApi, type RoutingGraph } from '../services/intakeRouting';
import styles from './IntakeRoutingPage.module.css';

cytoscape.use(dagre);

const DEFAULT_HINT =
  'Click an inbox, then a pipeline, to add/remove a route. Click a route line to delete it.';

const TONE = {
  inbox: { bg: '#e6f7ee', border: '#1f9d57', ink: '#0f5132' },
  pipeline_ready: { bg: '#fff4e0', border: '#d98300', ink: '#7a4a00' },
  pipeline_pending: { bg: '#e7f1fb', border: '#1d6fb8', ink: '#11456f' },
  kb: { bg: '#e1f5f2', border: '#0d9488', ink: '#0f5f57' },
} as const;

const LAYOUT = {
  name: 'dagre',
  rankDir: 'LR',
  nodeSep: 22,
  rankSep: 80,
  edgeSep: 10,
} as unknown as cytoscape.LayoutOptions;

const STYLE = [
  {
    selector: 'node',
    style: {
      label: 'data(label)',
      'text-wrap': 'wrap',
      'text-max-width': 150,
      'text-valign': 'center',
      'text-halign': 'center',
      'font-size': 10.5,
      'line-height': 1.18,
      width: 162,
      height: 56,
      shape: 'round-rectangle',
      padding: 6,
      'background-color': 'data(bg)',
      'border-color': 'data(border)',
      'border-width': 2,
      color: 'data(ink)',
    },
  },
  { selector: 'node[kind="kb"]', style: { shape: 'round-tag' } },
  { selector: 'node.sel', style: { 'border-width': 5, 'border-color': '#7c3aed' } },
  {
    selector: 'edge',
    style: { width: 2, 'curve-style': 'bezier', 'target-arrow-shape': 'triangle' },
  },
  {
    selector: 'edge[kind="route"]',
    style: {
      'line-color': '#7c3aed',
      'target-arrow-color': '#7c3aed',
      width: 2.8,
    },
  },
  {
    selector: 'edge[kind="tokb"]',
    style: { 'line-color': '#0d9488', 'target-arrow-color': '#0d9488' },
  },
] as unknown as cytoscape.Stylesheet[];

function buildElements(graph: RoutingGraph): ElementDefinition[] {
  const els: ElementDefinition[] = [];
  for (const ib of graph.inboxes) {
    const t = TONE.inbox;
    els.push({
      data: {
        id: `in:${ib.id}`,
        label: ib.email,
        kind: 'inbox',
        accountId: ib.id,
        bg: t.bg,
        border: t.border,
        ink: t.ink,
      },
    });
  }
  const kbSeen = new Set<string>();
  for (const p of graph.pipelines) {
    const t = p.extractor_ready ? TONE.pipeline_ready : TONE.pipeline_pending;
    els.push({
      data: {
        id: `pl:${p.key}`,
        label: `${p.label}\n(${p.key})`,
        kind: 'pipeline',
        pipelineKey: p.key,
        bg: t.bg,
        border: t.border,
        ink: t.ink,
      },
    });
    const kbId = `kb:${p.kb_server_id}`;
    if (!kbSeen.has(kbId)) {
      kbSeen.add(kbId);
      els.push({
        data: {
          id: kbId,
          label: p.kb_label,
          kind: 'kb',
          bg: TONE.kb.bg,
          border: TONE.kb.border,
          ink: TONE.kb.ink,
        },
      });
    }
    els.push({
      data: { id: `e_kb:${p.key}`, source: `pl:${p.key}`, target: kbId, kind: 'tokb' },
    });
  }
  for (const ib of graph.inboxes) {
    for (const key of ib.pipeline_keys) {
      els.push({
        data: {
          id: `r:${ib.id}:${key}`,
          source: `in:${ib.id}`,
          target: `pl:${key}`,
          kind: 'route',
          accountId: ib.id,
          pipelineKey: key,
        },
      });
    }
  }
  return els;
}

export function IntakeRoutingPage() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cyRef = useRef<Core | null>(null);
  const graphRef = useRef<RoutingGraph | null>(null);
  const selectedRef = useRef<string | null>(null);

  const [graph, setGraph] = useState<RoutingGraph | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string>(DEFAULT_HINT);

  const loadGraph = useCallback(async () => {
    try {
      const g = await intakeRoutingApi.graph();
      graphRef.current = g;
      setGraph(g);
      setError(null);
    } catch {
      setError('Failed to load the routing graph.');
    } finally {
      setLoading(false);
    }
  }, []);

  const clearSelection = useCallback(() => {
    selectedRef.current = null;
    setHint(DEFAULT_HINT);
    cyRef.current?.nodes('.sel').removeClass('sel');
  }, []);

  const toggleRoute = useCallback(
    async (accountId: string, pipelineKey: string) => {
      const inbox = graphRef.current?.inboxes.find((i) => i.id === accountId);
      const has = inbox?.pipeline_keys.includes(pipelineKey) ?? false;
      setBusy(true);
      try {
        if (has) await intakeRoutingApi.removeRoute(accountId, pipelineKey);
        else await intakeRoutingApi.addRoute(accountId, pipelineKey);
        clearSelection();
        await loadGraph();
      } catch {
        setError('Failed to update the route.');
      } finally {
        setBusy(false);
      }
    },
    [clearSelection, loadGraph],
  );

  // Keep the latest handlers reachable from the (once-wired) cytoscape callbacks.
  const actionsRef = useRef({ toggleRoute, clearSelection, setHint });
  actionsRef.current = { toggleRoute, clearSelection, setHint };

  useEffect(() => {
    void loadGraph();
  }, [loadGraph]);

  // Build/refresh the cytoscape graph whenever the data changes.
  useEffect(() => {
    if (!containerRef.current || !graph) return;
    const elements = buildElements(graph);

    if (!cyRef.current) {
      const cy = cytoscape({
        container: containerRef.current,
        elements,
        style: STYLE,
        layout: LAYOUT,
        wheelSensitivity: 0.2,
      });

      cy.on('tap', 'node', (evt: EventObject) => {
        const node = evt.target;
        const kind = node.data('kind') as string;
        if (kind === 'inbox') {
          const accountId = node.data('accountId') as string;
          cy.nodes('.sel').removeClass('sel');
          if (selectedRef.current === accountId) {
            actionsRef.current.clearSelection();
            return;
          }
          node.addClass('sel');
          selectedRef.current = accountId;
          actionsRef.current.setHint(
            `Selected ${node.data('label')} — click a pipeline to add/remove its route.`,
          );
        } else if (kind === 'pipeline') {
          const sel = selectedRef.current;
          if (sel) {
            void actionsRef.current.toggleRoute(sel, node.data('pipelineKey') as string);
          } else {
            actionsRef.current.setHint(
              'Click an inbox first, then a pipeline, to wire a route.',
            );
          }
        }
      });

      cy.on('tap', 'edge', (evt: EventObject) => {
        const edge = evt.target;
        if ((edge.data('kind') as string) !== 'route') return;
        void actionsRef.current.toggleRoute(
          edge.data('accountId') as string,
          edge.data('pipelineKey') as string,
        );
      });

      cy.on('tap', (evt: EventObject) => {
        if (evt.target === cy) actionsRef.current.clearSelection();
      });

      cyRef.current = cy;
    } else {
      const cy = cyRef.current;
      cy.elements().remove();
      cy.add(elements);
      cy.layout(LAYOUT).run();
      if (selectedRef.current) {
        cy.getElementById(`in:${selectedRef.current}`).addClass('sel');
      }
    }
  }, [graph]);

  // Tear down on unmount.
  useEffect(
    () => () => {
      cyRef.current?.destroy();
      cyRef.current = null;
    },
    [],
  );

  const fit = useCallback(() => cyRef.current?.fit(undefined, 26), []);

  const pipelineLabel = useCallback(
    (key: string): string => {
      const p = graph?.pipelines.find((x) => x.key === key);
      return p ? p.label : key;
    },
    [graph],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Inbox Routing</h1>
        <p>
          Route each inbox to one or more extraction pipelines (each writes its own
          Knowledge Bank). The graph is the editor — click an inbox, then a pipeline,
          to toggle a route; click a purple route line to delete it.
        </p>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.toolbar}>
        <button type="button" className={styles.btn} onClick={fit}>
          Fit
        </button>
        <button
          type="button"
          className={styles.btn}
          onClick={() => void loadGraph()}
        >
          Reload
        </button>
        {busy && <span className={styles.busy}>saving…</span>}
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={`${styles.sw} ${styles.swInbox}`} />
            Inbox
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.sw} ${styles.swRoute}`} />
            Route
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.sw} ${styles.swReady}`} />
            Pipeline (live)
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.sw} ${styles.swPending}`} />
            Pipeline (extractor pending)
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.sw} ${styles.swKb}`} />
            Knowledge Bank
          </span>
        </div>
      </div>

      <div className={styles.hint}>{hint}</div>

      <div ref={containerRef} className={styles.cy} />

      {loading ? (
        <div className={styles.state}>Loading routing graph…</div>
      ) : graph && graph.inboxes.length === 0 ? (
        <div className={styles.summary}>
          No connected inboxes yet. Connect a Gmail/Outlook account under{' '}
          <strong>Email Setup</strong>, then route it here.
        </div>
      ) : graph ? (
        <div className={styles.summary}>
          <h3>Current routing</h3>
          {graph.inboxes.map((ib) => (
            <div className={styles.row} key={ib.id}>
              <span className={styles.ib}>{ib.email}</span>
              {' → '}
              {ib.pipeline_keys.length > 0 ? (
                ib.pipeline_keys.map((k, idx) => (
                  <span key={k}>
                    {idx > 0 ? ', ' : ''}
                    <span className={styles.pl}>{pipelineLabel(k)}</span>
                  </span>
                ))
              ) : (
                <span className={styles.none}>(no routes)</span>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
